/**
 * Clear button visibility and hero search auto-focus on arrival.
 *
 * @since 1.0.0
 */
/* global sessionStorage */

const focusSearchKey = 'gmrFocusSearch';

/**
 * Whether the current URL represents a committed search (non-empty `s` param).
 *
 * @return {boolean}
 */
function urlHasSearchQuery() {
	return Boolean(new URL(window.location.href).searchParams.get('s')?.trim());
}

/**
 * Update filled / searched classes and clear-button visibility for a search input wrap.
 *
 * @param {HTMLInputElement} input Search input element.
 * @param {boolean} [isSearched] Optional override; defaults to URL `s` param.
 */
export function updateSearchFieldState(input, isSearched) {
	const wrap = input.closest('.search-form-input-wrap');

	if (!wrap) {
		return;
	}

	const hasValue = input.value.trim().length > 0;
	const searched =
		typeof isSearched === 'boolean' ? isSearched && hasValue : urlHasSearchQuery() && hasValue;

	wrap.classList.toggle('has-value', hasValue);
	wrap.classList.toggle('is-searched', searched);

	const clearButton = wrap.querySelector('.js-search-form-clear');

	if (clearButton) {
		clearButton.hidden = !hasValue;
	}
}

/**
 * Sync search field state for every search input on the page.
 *
 * @param {boolean} [isSearched] Optional override; defaults to URL `s` param.
 */
export function syncSearchFieldStateFromUrl(isSearched) {
	document.querySelectorAll('.search-form-input[name="s"]').forEach(function (input) {
		updateSearchFieldState(input, isSearched);
	});
}

/**
 * Sync all search inputs on the page to the same value.
 *
 * @param {string} value Search query value.
 */
function syncSearchInputs(value) {
	document.querySelectorAll('.search-form-input[name="s"]').forEach(function (input) {
		input.value = value;
		updateSearchFieldState(input, value.trim().length > 0 ? urlHasSearchQuery() : false);
	});
}

/**
 * Initialize clear buttons on hero search forms.
 */
function initClearButtons() {
	document.querySelectorAll('.search-form-input[name="s"]').forEach(function (input) {
		updateSearchFieldState(input);

		input.addEventListener('input', function () {
			const value = input.value;

			document.querySelectorAll('.search-form-input[name="s"]').forEach(function (otherInput) {
				if (otherInput !== input) {
					otherInput.value = value;
				}

				updateSearchFieldState(otherInput);
			});
		});
	});

	document.querySelectorAll('.js-search-form-clear').forEach(function (button) {
		button.addEventListener('click', function () {
			syncSearchInputs('');

			const heroForm = document.querySelector('.search-form--hero[data-gmr-async-form]');

			if (heroForm) {
				const pageField = heroForm.querySelector('input[name="paged"]');

				if (pageField) {
					pageField.value = '1';
				}

				heroForm.dispatchEvent(new CustomEvent('gmr-async-request'));
			}

			document.querySelector('.hero-container-search-form .search-form-input')?.focus();
		});
	});

	document.querySelectorAll('.js-search-form-submit').forEach(function (button) {
		button.addEventListener('click', function () {
			const form = button.closest('.search-form--hero[data-gmr-async-form]');

			if (!form) {
				return;
			}

			form.dispatchEvent(new CustomEvent('gmr-async-request'));
		});
	});
}

/**
 * Set sessionStorage flag when navigating to search from the header link.
 */
function initHeaderSearchFocus() {
	document.querySelectorAll('.btn-menu-search-link').forEach(function (link) {
		link.addEventListener('click', function () {
			if (document.body.classList.contains('search')) {
				return;
			}

			sessionStorage.setItem(focusSearchKey, '1');
		});
	});
}

/**
 * Focus the hero search input when arriving from a header redirect.
 */
function initSearchAutoFocus() {
	if (!document.body.classList.contains('search')) {
		return;
	}

	if (sessionStorage.getItem(focusSearchKey) !== '1') {
		return;
	}

	sessionStorage.removeItem(focusSearchKey);

	const heroInput = document.querySelector('.hero-container-search-form .search-form-input');

	if (heroInput) {
		heroInput.focus();
	}
}

/**
 * Initialize clear button and auto-focus behavior.
 */
export function initSearchFormClear() {
	initClearButtons();
	initHeaderSearchFocus();
	initSearchAutoFocus();
	syncSearchFieldStateFromUrl();
}
